/** Which offboarding items stand between a closed tenant and the release of its code
 *  (tenant retirement CONTRACT §9), and why — the Offboarding tab's "Blocks release" marker.
 *
 *  Whether an action can block is the backend's `blocks_release` on the item, and whether the
 *  release gate checks its condition again live is its `live_release_check` (both from the action
 *  class). The release gate itself is the backend's too (`offboarding_incomplete`); this only
 *  says which rows to mark. */
import type { PreflightItem } from '@/api/preflight';

import { isOpenStatus } from '@/features/preflight/status';

const UNTIL_DONE = 'Must be done, or skipped with a reason, before the code can be released.';

const WHILE_IT_HOLDS =
  'The release checks this again: it blocks while its condition holds, even once done — run it (again), or skip it with a reason.';

/** Why this item blocks the release now, or null when it does not (any more):
 *  - never unless the backend says its action can (`blocks_release`);
 *  - otherwise while it is open work (pending / started / error);
 *  - a live-check item (`live_release_check`) also once completed — the backend checks its
 *    condition again whatever the status — until it is skipped;
 *  - never once superseded (the tenant was reopened; a re-close puts it back to pending). */
export function releaseBlockingReason(
  item: Pick<PreflightItem, 'status' | 'blocks_release' | 'live_release_check'>,
): string | null {
  if (!item.blocks_release) return null;
  if (item.live_release_check === true)
    return isOpenStatus(item.status) || item.status === 'completed' ? WHILE_IT_HOLDS : null;
  return isOpenStatus(item.status) ? UNTIL_DONE : null;
}
